import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useAdjustmentEventForLateFeeOffsetApplicationsCreate } from "gen/endpoints/fee-charge-event/fee-charge-event";
import { FundFamilyEventData } from "gen/model/fundFamilyEventData";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { rootRoute } from "shared/navigation/Routes";

export interface LateOffsetsDateModalProps {
  fundFamilyId: string;
  feeChargeEventId: string;
  feeChargeDate: string;
  onCloseComplete(): void;
}

export const LateOffsetsDateModal: React.FC<LateOffsetsDateModalProps> = ({
  fundFamilyId,
  feeChargeEventId,
  feeChargeDate,
  onCloseComplete,
}) => {
  const { t } = useTranslation(["fees", "common"]);
  const navigate = useNavigate();

  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  const form = useForm({
    defaultValues: {
      effective_date: feeChargeDate,
    },
  });

  const { mutateAsync: createAdjustmentEvent } =
    useAdjustmentEventForLateFeeOffsetApplicationsCreate();

  const onSubmit = async () =>
    await createAdjustmentEvent({ fundFamilyId, feeChargeEventId });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={(data: FundFamilyEventData) => {
            onClose();
            navigate(
              rootRoute({})
                .fundFamilies({})
                .fundFamily({ fundFamilyId })
                .fees({})
                .lateOffsets({
                  feeChargeEventId,
                  adjustmentFundFamilyEventId: data.id!,
                })
                .applyOffsets({}).$,
            );
          }}
        >
          <ModalHeader>
            {t("fees:lateOffsets.dateModal.title")}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <FormDateInput
              name="effective_date"
              title={t("common:effectiveDate")}
              isReadOnly={true}
              showIcon={false}
              controlProps={{
                paddingBottom: "8px",
              }}
            />
            <Text size="14px">
              {t("fees:lateOffsets.dateModal.description")}
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} autoFocus={true} />}
              confirm={
                <FormSubmitButton>
                  {t("fees:lateOffsets.dateModal.button")}
                </FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
